import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import {
    AuctionBuilderBudgetErrorCode,
    AuctionCampaign,
    AuctionDataField,
    auctionSelector,
    auctionSelectorNonNullable,
    clearAuctionErrors,
    setErrors,
} from 'store/models/auctionData';
import { useSelector } from 'store/utils';

type ValidateBudgetFn = (budgetValue: number | null) => boolean;

export const getBudgetValidationError = (
    budgetValue: number | null,
    minValue: number,
    auctionCampaign: AuctionCampaign | null
): AuctionBuilderBudgetErrorCode | false => {
    if (budgetValue === null) {
        return AuctionBuilderBudgetErrorCode.ValueMissing;
    }
    if (budgetValue < minValue) {
        return AuctionBuilderBudgetErrorCode.RangeUnderflow;
    }

    if (auctionCampaign && budgetValue < auctionCampaign.budget - auctionCampaign.campaignBalance) {
        return AuctionBuilderBudgetErrorCode.BudgetCanNotBeLessThanAlreadySpent;
    }
    return false;
};

export const useAuctionBudgetValidation = (): ValidateBudgetFn => {
    const minValue = useSelector(auctionSelectorNonNullable.budget).minValue;
    const auctionCampaign = useSelector(auctionSelector.auctionCampaign);

    const dispatch = useDispatch();

    return useCallback<ValidateBudgetFn>(
        (budgetValue: number | null) => {
            const errorCode = getBudgetValidationError(budgetValue, minValue, auctionCampaign);

            if (errorCode) {
                dispatch(setErrors({ budget: errorCode }));
                return false;
            }
            dispatch(clearAuctionErrors(AuctionDataField.Budget));
            return true;
        },
        [minValue, auctionCampaign, dispatch]
    );
};
